import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Boxes, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { libraryInventoryApi, libraryLocationsApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { InventoryAudit } from '@/types/library';
import { formatDate, orDash } from '@/utils/format';
import { AuditStatusBadge, Stat } from './shared';

/**
 * What the school holds, and the counts of it.
 *
 * <p>MISSING has its own tile, apart from LOST, because they are different
 * facts. Lost means somebody is being charged for a book; missing means the
 * shelf was searched and it was not there. A count never deletes anything, and a
 * book missing in June is very often found in September behind the wrong shelf.
 */
export function InventoryPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('startedOn,desc');
  const manages = can(PERMISSIONS.LIBRARY_INVENTORY_MANAGE);

  const [starting, setStarting] = useState(false);
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');

  const summary = useQuery({
    queryKey: queryKeys.libraryInventory.summary(undefined),
    queryFn: () => libraryInventoryApi.summary(),
  });

  const audits = useQuery({
    queryKey: queryKeys.libraryInventory.audits(table.params),
    queryFn: () => libraryInventoryApi.audits(table.params),
  });

  const locations = useQuery({
    queryKey: queryKeys.library.locations(undefined),
    queryFn: () => libraryLocationsApi.list(),
    enabled: starting,
  });

  const start = useMutation({
    mutationFn: () =>
      libraryInventoryApi.startAudit({
        name: name.trim() === '' ? undefined : name.trim(),
        locationId: locationId === '' ? undefined : locationId,
      }),
    onSuccess: (audit) => {
      toast.success(
        `${audit.reference} started — ${audit.expectedCount} cop${
          audit.expectedCount === 1 ? 'y' : 'ies'
        } to find`,
      );
      setStarting(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.libraryInventory.all });
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<InventoryAudit>[] = [
    {
      key: 'reference',
      header: 'Count',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.inventoryAudit(row.id)}
            className="block font-mono text-[12px] text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.reference}
          </Link>
          <span className="block text-[11px] text-subtle">{row.name}</span>
        </span>
      ),
    },
    { key: 'location', header: 'Shelves', render: (row) => orDash(row.locationPath ?? 'Whole library') },
    { key: 'startedOn', header: 'Started', sortKey: 'startedOn', render: (row) => formatDate(row.startedOn) },
    {
      key: 'expected',
      header: 'Expected',
      className: 'text-right',
      render: (row) => row.expectedCount,
    },
    { key: 'seen', header: 'Found', className: 'text-right', render: (row) => row.seen },
    {
      key: 'missing',
      header: 'Not found',
      className: 'text-right',
      render: (row) =>
        row.missing > 0 ? (
          <span className="font-semibold text-[color:var(--sms-warning)]">{row.missing}</span>
        ) : (
          row.missing
        ),
    },
    { key: 'status', header: 'Status', render: (row) => <AuditStatusBadge status={row.status} /> },
  ];

  const stock = summary.data;

  return (
    <>
      <PageHeader
        title="Stock"
        description="What the library holds, and every count of it."
        icon={<Boxes className="h-5 w-5" aria-hidden="true" />}
        action={
          manages ? (
            <Button onClick={() => setStarting(true)}>
              <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
              Start a count
            </Button>
          ) : undefined
        }
      />

      {summary.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        stock !== undefined && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Stat
                label="Titles"
                value={stock.totalTitles}
                hint={`${stock.totalCopies} physical copies`}
              />
              <Stat label="On the shelf" value={stock.available} tone="success" />
              <Stat label="Out" value={stock.issued} hint={`${stock.reserved} held for somebody`} />
              <Stat
                label="Out of circulation"
                value={stock.outOfCirculation}
                hint="Withdrawn or archived"
              />
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Stat
                label="Missing"
                value={stock.missing}
                tone={stock.missing > 0 ? 'warning' : 'neutral'}
                hint="Searched for and not found. Nobody has been charged."
              />
              <Stat
                label="Lost"
                value={stock.lost}
                tone={stock.lost > 0 ? 'danger' : 'neutral'}
                hint="Written off, usually with a charge behind it"
              />
              <Stat
                label="Damaged"
                value={stock.damaged}
                tone={stock.damaged > 0 ? 'warning' : 'neutral'}
                hint="Damaged or being repaired"
              />
            </div>
          </>
        )
      )}

      <Card className="mt-4">
        <div className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Stock counts
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            A count records what was on the shelves on a date. It never deletes a copy.
          </p>
        </div>
        <DataTable
          columns={columns}
          rows={audits.data?.data}
          rowKey={(row) => row.id}
          loading={audits.isPending}
          error={audits.error}
          onRetry={() => void audits.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="Never counted"
          emptyDescription="Start a count to check the shelves against the catalogue."
        />
      </Card>

      <Modal
        open={starting}
        onClose={() => setStarting(false)}
        title="Start a stock count"
        description="Snapshots what should be on the shelves. Books out on loan are left out — they are not there and are not supposed to be."
        footer={
          <>
            <Button variant="secondary" onClick={() => setStarting(false)}>
              Cancel
            </Button>
            <Button onClick={() => start.mutate()} disabled={start.isPending}>
              Start counting
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField
            label="What to call it"
            value={name}
            onChange={(event) => setName(event.target.value)}
            hint="Left blank, it is named after today's date."
          />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Which shelves
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">The whole library</option>
              {(locations.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.fullPath}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-muted">
              Counting one section at a time is usually how it is actually done.
            </span>
          </label>
        </div>
      </Modal>
    </>
  );
}
